import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

/**
 * Returns a trigger that invalidates `keys` once, `delayMs` after the last call,
 * but never later than `maxWaitMs` after the first pending call (so a steady
 * stream of events cannot starve the refresh).
 */
export function useDebouncedInvalidate(keys: QueryKey[], delayMs = 1000, maxWaitMs = 5000): () => void {
  const queryClient = useQueryClient();
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const firstAt = useRef<number | null>(null);
  const keysRef = useRef(keys);
  keysRef.current = keys;
  useEffect(() => () => clearTimeout(timer.current), []);
  return useCallback(() => {
    clearTimeout(timer.current);
    const now = Date.now();
    if (firstAt.current === null) firstAt.current = now;
    const wait = Math.max(0, Math.min(delayMs, firstAt.current + maxWaitMs - now));
    timer.current = setTimeout(() => {
      firstAt.current = null;
      for (const queryKey of keysRef.current) void queryClient.invalidateQueries({ queryKey });
    }, wait);
  }, [queryClient, delayMs, maxWaitMs]);
}
